import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "sidebar-collapsed"

// Desktop-only icon rail toggle for .app-shell. Applied on connect (which
// fires on every Turbo page load) so the preference survives navigation.
export default class extends Controller {
  static targets = ["toggle"]

  connect() {
    this.apply(this.preferredState())

    this.sidebar = this.element.querySelector(".app-sidebar")
    this.topbar = this.element.querySelector(".app-topbar")
    this.updateSidebarPosition = this.updateSidebarPosition.bind(this)
    this.scheduleSidebarPosition = this.scheduleSidebarPosition.bind(this)
    window.addEventListener("scroll", this.scheduleSidebarPosition, { passive: true })
    window.addEventListener("resize", this.scheduleSidebarPosition)
    this.scheduleSidebarPosition()
  }

  disconnect() {
    window.removeEventListener("scroll", this.scheduleSidebarPosition)
    window.removeEventListener("resize", this.scheduleSidebarPosition)
    if (this.sidebarPositionFrame) window.cancelAnimationFrame(this.sidebarPositionFrame)
    this.sidebarPositionFrame = null
    this.sidebar?.style.removeProperty("transform")
    this.sidebar?.style.removeProperty("--sidebar-top")
  }

  toggle() {
    this.apply(!this.element.classList.contains("app-shell--collapsed"))

    try {
      window.localStorage.setItem(STORAGE_KEY, this.collapsed ? "1" : "0")
    } catch {
      // Collapsing still works when storage is unavailable, it just won't persist.
    }
  }

  apply(collapsed) {
    this.collapsed = collapsed
    this.element.classList.toggle("app-shell--collapsed", collapsed)
    this.toggleTargets.forEach((el) => el.setAttribute("aria-expanded", String(!collapsed)))
  }

  preferredState() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1"
    } catch {
      return false
    }
  }

  scheduleSidebarPosition() {
    if (this.sidebarPositionFrame) return

    this.sidebarPositionFrame = window.requestAnimationFrame(this.updateSidebarPosition)
  }

  updateSidebarPosition() {
    this.sidebarPositionFrame = null
    if (!this.sidebar) return

    if (window.innerWidth < 992 || document.body.classList.contains("admin-app")) {
      this.sidebar.style.removeProperty("transform")
      this.sidebar.style.removeProperty("--sidebar-top")
      return
    }

    // Banners can sit above the topbar; follow its actual viewport position.
    const topbarBottom = this.topbar?.getBoundingClientRect().bottom ?? 0
    const sidebarTop = Math.max(0, Math.min(window.innerHeight, topbarBottom))
    this.sidebar.style.setProperty("--sidebar-top", `${sidebarTop}px`)

    // Keep the fixed sidebar inside the app shell as its footer approaches.
    const overlap = Math.max(0, window.innerHeight - this.element.getBoundingClientRect().bottom)
    this.sidebar.style.transform = overlap ? `translateY(-${overlap}px)` : ""
  }
}
